import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Palette, Loader2, Save, LayoutTemplate, Sidebar } from 'lucide-react'
import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Input } from '../components/ui/input'
import { themeService, Theme } from '../services/theme.service'

const THEME_CATEGORIES = [
    {
        id: 'layout',
        title: 'Layout (Base)',
        icon: LayoutTemplate,
        description: 'Colores principales para la estructura, fondos y textos de la aplicación.',
        properties: [
            { key: 'layout-bg', label: 'Fondo de Pantalla', default: '#f8fafc' },
            { key: 'layout-surface', label: 'Fondo de Tarjetas/Paneles', default: '#ffffff' },
            { key: 'layout-text-primary', label: 'Color de Texto Principal', default: '#0f172a' },
            { key: 'layout-text-secondary', label: 'Color de Texto Secundario', default: '#64748b' },
            { key: 'layout-border', label: 'Color de Bordes', default: '#e2e8f0' },
        ]
    },
    {
        id: 'sidebar',
        title: 'Sidebar (Navegación)',
        icon: Sidebar,
        description: 'Colores exclusivos para el menú lateral.',
        properties: [
            { key: 'sidebar-bg', label: 'Fondo del Sidebar', default: '#ffffff' },
            { key: 'sidebar-text', label: 'Texto e Íconos', default: '#475569' },
            { key: 'sidebar-hover-bg', label: 'Fondo al pasar el mouse (Hover)', default: '#f1f5f9' },
            { key: 'sidebar-item-active-bg', label: 'Fondo de Menú Activo', default: '#e0f2fe' },
            { key: 'sidebar-item-active-text', label: 'Texto de Menú Activo', default: '#0369a1' },
        ]
    }
]

export function Themes() {
    const queryClient = useQueryClient()
    
    // El estado local de las propiedades para reflejar los cambios en tiempo real
    const [properties, setProperties] = useState<Record<string, string>>({})

    const { data: theme, isLoading } = useQuery({
        queryKey: ['activeTheme'],
        queryFn: themeService.getCurrentTheme
    })

    useEffect(() => {
        if (theme && theme.properties) {
            const props: Record<string, string> = {}
            theme.properties.forEach(p => {
                props[p.key] = p.value.value as string
            })
            setProperties(props)
        }
    }, [theme])

    const updateMutation = useMutation({
        mutationFn: () => {
            const formattedProps: Record<string, { type: string, value: any }> = {}
            Object.entries(properties).forEach(([k, v]) => {
                formattedProps[k] = { type: 'color', value: v }
            })
            return themeService.updateCurrentTheme(formattedProps)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['activeTheme'] })
            toast.success('Configuración de tema guardada correctamente')
        },
        onError: () => toast.error('Error al guardar el tema')
    })

    const handleChange = (key: string, value: string) => {
        setProperties(prev => ({ ...prev, [key]: value }))
        // Inyectar al CSS :root en tiempo real
        document.documentElement.style.setProperty(`--${key}`, value)
    }

    if (isLoading) {
        return (
            <div className="flex h-full items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-[var(--primary)]" />
            </div>
        )
    }

    return (
        <div className="min-h-full bg-[var(--layout-bg,var(--surface-base,#f8fafc))] px-4 py-6 sm:px-6 lg:px-8">
            <div className="mx-auto flex max-w-4xl flex-col gap-6">
                <div className="flex items-center justify-between">
                    <div className="flex flex-col gap-2">
                        <h1 className="text-2xl font-semibold text-[var(--layout-text-primary,var(--text-primary,#0f172a))]">
                            Tema de la Compañía
                        </h1>
                        <p className="text-sm text-[var(--layout-text-secondary,var(--text-secondary,#64748b))]">
                            Personaliza la identidad visual de la plataforma. Los cambios se previsualizan instantáneamente.
                        </p>
                    </div>
                    <Button onClick={() => updateMutation.mutate()} disabled={updateMutation.isPending}>
                        {updateMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                        Guardar Tema
                    </Button>
                </div>

                <div className="grid gap-6">
                    {THEME_CATEGORIES.map((category) => (
                        <Card key={category.id} className="border-[var(--layout-border,var(--border-default,#e2e8f0))] bg-[var(--layout-surface,var(--surface-panel,#ffffff))]">
                            <CardHeader className="border-b border-[var(--layout-border,var(--border-default,#e2e8f0))] pb-4">
                                <CardTitle className="flex items-center gap-2 text-lg text-[var(--layout-text-primary,var(--text-primary,#0f172a))]">
                                    <category.icon className="h-5 w-5 text-[var(--primary)]" />
                                    {category.title}
                                </CardTitle>
                                <CardDescription className="text-[var(--layout-text-secondary,var(--text-secondary,#64748b))]">
                                    {category.description}
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="grid gap-4 pt-6 sm:grid-cols-2">
                                {category.properties.map((prop) => (
                                    <div key={prop.key} className="space-y-2">
                                        <label className="text-sm font-medium text-[var(--layout-text-primary,var(--text-primary,#0f172a))]">
                                            {prop.label}
                                        </label>
                                        <div className="flex gap-2">
                                            <input
                                                type="color"
                                                value={properties[prop.key] || prop.default}
                                                onChange={(e) => handleChange(prop.key, e.target.value)}
                                                className="h-10 w-12 rounded-md border border-[var(--layout-border,var(--border-default,#e2e8f0))] bg-transparent p-1 cursor-pointer"
                                            />
                                            <Input 
                                                value={properties[prop.key] || prop.default}
                                                onChange={(e) => handleChange(prop.key, e.target.value)}
                                                className="font-mono text-[var(--layout-text-primary,var(--text-primary,#0f172a))] border-[var(--layout-border,var(--border-default,#e2e8f0))]"
                                            />
                                        </div>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>
                    ))}
                </div>
            </div>
        </div>
    )
}
